import * as Sentry from '@sentry/react'
import buildInfo from './buildInfo.json'

// Lowercased to match the backend, which sets its environment from Rails.env. Sentry compares
// environments as case-sensitive strings, so PRODUCTION and production would be two separate
// environments in the same org and no shared filter, alert or dashboard would show both.
const ENVIRONMENT = (process.env.REACT_APP_ENVIRONMENT || 'development').toLowerCase()

// What axios reports when a request never got a response. A dropped warehouse wifi and a full
// backend outage are indistinguishable here, so these are sampled rather than suppressed.
const CONNECTIVITY_MESSAGES = ['Network Error', 'Request aborted', 'timeout of']

// Enough for an outage to show up, not enough for a bad shift to spend the replay quota.
const CONNECTIVITY_SAMPLE_RATE = 0.05

// Browser extensions on warehouse Chrome profiles (scanner and kiosk helpers) throw from
// frames we cannot read or fix, and at replaysOnErrorSampleRate 1.0 each one costs a replay.
const DENY_URLS = [/^chrome-extension:\/\//i, /^moz-extension:\/\//i, /^chrome:\/\//i, /clarity\.ms/i]

// Route URLs carry credentials, not just identifiers: the handheld pairing screen is reached
// as /handheld/link?code=<pairing code>. Sentry's HttpContext integration puts the full
// location.href on event.request.url, so it has to be scrubbed there too, not only on
// breadcrumbs.
const stripQuery = url => (typeof url === 'string' ? url.split('?')[0] : url)

// This module is imported first from index.js, before ./App and the rest of the graph.
// ES imports are evaluated before the importing module's own body, so an init() call
// written inside index.js would only run once every other module had already had its
// chance to throw at module scope — and a boot-time throw is exactly the blank screen
// this exists to catch.
//
// Without a DSN Sentry stays inert, so local dev and any deploy that has not had the env
// var set yet simply do nothing instead of failing to boot.
Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  environment: ENVIRONMENT,
  // Which bundle threw. Tabs stay open across deploys on the warehouse floor, so without this an
  // error from a build fixed hours ago is indistinguishable from a regression in the current one —
  // and StaleBundleGuard's whole job is to shrink that population, which needs to be measurable.
  initialScope: { tags: { build: buildInfo.build } },
  integrations: [Sentry.replayIntegration()],
  // Replay only the sessions that actually broke. Recording every warehouse shift is the
  // always-on session cost we are deliberately not taking on.
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,
  // The "completed with undelivered notifications" variant is already in the SDK defaults,
  // which are merged with this list rather than replaced. Chrome's variant is not.
  ignoreErrors: ['ResizeObserver loop limit exceeded'],
  denyUrls: DENY_URLS,
  beforeSend(event, hint) {
    if (event.request?.url) event.request.url = stripQuery(event.request.url)

    // Coerced: a rejection carrying a non-string message would otherwise throw inside this
    // hook, and the SDK replaces the real event with that TypeError rather than reporting it.
    const message = String(hint?.originalException?.message ?? event.message ?? '')
    if (!CONNECTIVITY_MESSAGES.some(candidate => message.includes(candidate))) return event

    // The terminal itself lost wifi. Constant in a warehouse, and there is nothing to learn.
    if (navigator.onLine === false) return null

    // The browser had a connection and the API still could not be reached, which is the
    // shape of a real outage. Keep a fraction under one fingerprint so a spike is visible
    // as a single issue instead of a flood or, worse, nothing at all.
    if (Math.random() > CONNECTIVITY_SAMPLE_RATE) return null
    event.fingerprint = ['api-unreachable']
    return event
  },
  beforeBreadcrumb(breadcrumb) {
    // Replay's masking does not extend to breadcrumbs. Console output is free text nobody
    // vetted, and WMS request URLs carry order and tracking numbers in their query strings.
    if (breadcrumb.category === 'console') return null
    if ((breadcrumb.category === 'xhr' || breadcrumb.category === 'fetch') && breadcrumb.data?.url) {
      breadcrumb.data.url = breadcrumb.data.url.split('?')[0]
    }
    return breadcrumb
  },
})

// A missing DSN is silent by design: Sentry drops every event without complaining, which on
// a deployed environment is indistinguishable from "no errors happened".
if (!process.env.REACT_APP_SENTRY_DSN && ENVIRONMENT !== 'development') {
  console.warn('REACT_APP_SENTRY_DSN is not set: frontend errors are not being reported.')
}
